import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { EmailHtmlIssue, EmailHtmlIssueSeverity, EmailImageResult } from 'src/app/models/communication/email-html-import';

const SEVERITY_ORDER: Record<EmailHtmlIssueSeverity, number> = { error: 0, warning: 1, info: 2 };
const TAG_SEVERITY: Record<EmailHtmlIssueSeverity, 'danger' | 'warning' | 'info'> = {
	error: 'danger',
	warning: 'warning',
	info: 'info'
};
const TAG_LABEL: Record<EmailHtmlIssueSeverity, string> = { error: 'Error', warning: 'Warning', info: 'Info' };

@Component({
	selector: 'app-email-import-issues',
	templateUrl: './email-import-issues.component.html',
	styleUrls: ['./email-import-issues.component.scss']
})
export class EmailImportIssuesComponent implements OnChanges {
	@Input() issues: EmailHtmlIssue[] = [];
	@Input() images: EmailImageResult[] = [];

	public imagesExpanded: boolean = false;
	/** The panel is a one-line summary until opened, so it doesn't eat editor height. */
	public expanded: boolean = false;
	private lastBlockingCount = 0;

	ngOnChanges(changes: SimpleChanges): void {
		// Open the image list automatically when something failed, so the failure is not hidden.
		if (changes['images'] && this.failedImageCount > 0) {
			this.imagesExpanded = true;
		}
		// Open the panel when errors first appear; after that the user's open/closed choice sticks
		// (lint re-runs on every edit, and re-opening each time would fight them).
		const blocking = this.errorCount + this.failedImageCount;
		if (blocking > 0 && this.lastBlockingCount === 0) {
			this.expanded = true;
		}
		this.lastBlockingCount = blocking;
	}

	get hasContent(): boolean {
		return (this.issues?.length ?? 0) > 0 || (this.images?.length ?? 0) > 0;
	}

	get errorCount(): number {
		return this.countSeverity('error');
	}

	get warningCount(): number {
		return this.countSeverity('warning');
	}

	get infoCount(): number {
		return this.countSeverity('info');
	}

	get hostedImageCount(): number {
		return (this.images ?? []).filter((i) => !i.error && !!i.hosted).length;
	}

	get failedImageCount(): number {
		return (this.images ?? []).filter((i) => !!i.error).length;
	}

	/** Errors first, then warnings, then info; original order kept within a severity. */
	get sortedIssues(): EmailHtmlIssue[] {
		return (this.issues ?? [])
			.map((issue, index) => ({ issue, index }))
			.sort((a, b) => (SEVERITY_ORDER[a.issue.severity] ?? 3) - (SEVERITY_ORDER[b.issue.severity] ?? 3) || a.index - b.index)
			.map((x) => x.issue);
	}

	get summary(): string {
		const parts: string[] = [];
		const issueParts: string[] = [];
		if (this.errorCount) issueParts.push(this.plural(this.errorCount, 'error'));
		if (this.warningCount) issueParts.push(this.plural(this.warningCount, 'warning'));
		if (this.infoCount) issueParts.push(`${this.infoCount} info`);
		if (issueParts.length) parts.push(issueParts.join(', '));

		if (this.images?.length) {
			const hosted = this.hostedImageCount;
			const failed = this.failedImageCount;
			const unchanged = this.images.length - hosted - failed;
			const imageParts = [`${this.plural(hosted, 'image')} hosted`];
			if (failed) imageParts.push(`${failed} failed`);
			if (unchanged) imageParts.push(`${unchanged} unchanged`);
			parts.push(imageParts.join(', '));
		}
		return parts.join(' · ');
	}

	public tagSeverity(severity: EmailHtmlIssueSeverity): 'danger' | 'warning' | 'info' {
		return TAG_SEVERITY[severity] ?? 'info';
	}

	public tagLabel(severity: EmailHtmlIssueSeverity): string {
		return TAG_LABEL[severity] ?? severity;
	}

	/** Shortens long URLs in the middle so both the host and the file name stay visible. */
	public truncateMiddle(value: string, max: number = 60): string {
		if (!value || value.length <= max) {
			return value;
		}
		const keep = max - 1;
		const head = Math.ceil(keep / 2);
		const tail = Math.floor(keep / 2);
		return `${value.slice(0, head)}…${value.slice(value.length - tail)}`;
	}

	public trackIssue(index: number, issue: EmailHtmlIssue): string {
		return `${issue.severity}|${issue.code}|${issue.message}|${index}`;
	}

	private countSeverity(severity: EmailHtmlIssueSeverity): number {
		return (this.issues ?? []).filter((i) => i.severity === severity).length;
	}

	private plural(count: number, noun: string): string {
		return `${count} ${noun}${count === 1 ? '' : 's'}`;
	}
}
